import React, { useContext, createContext, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { useMount } from 'ahooks'

import { SIGN_IN, GET_CURRENT_USER, CHANGE_PASSWORD } from 'query'

const ClientUserContext = createContext(null)

const AUTH_TOKEN_KEY = '@authToken'
const ACCOUNT_LIST_KEY = '@authAccounts'
const ACTIVE_ACCOUNT_KEY = '@activeAccountId'

const readStoredAccounts = () => {
  try {
    const raw = localStorage.getItem(ACCOUNT_LIST_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch (err) {
    console.log('ClientUserProvider readStoredAccounts', err)
    return []
  }
}

const ClientUserProvider = ({ children }) => {
  const apolloClient = useApolloClient()

  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [clientUserData, setClientUserData] = useState(null)
  const [store, setStore] = useState({})
  const [userLoading, setUserLoading] = useState(false)
  const [accounts, setAccounts] = useState(readStoredAccounts())
  const [activeAccountId, setActiveAccountId] = useState(localStorage.getItem(ACTIVE_ACCOUNT_KEY))

  const persistAccounts = (nextAccounts) => {
    setAccounts(nextAccounts)
    localStorage.setItem(ACCOUNT_LIST_KEY, JSON.stringify(nextAccounts))
  }

  const persistActiveAccount = (accountId) => {
    setActiveAccountId(accountId)
    if (accountId) {
      localStorage.setItem(ACTIVE_ACCOUNT_KEY, accountId)
    } else {
      localStorage.removeItem(ACTIVE_ACCOUNT_KEY)
    }
  }

  const normalizeAccount = (data) => {
    if (!data?._id || !data?.token) return null
    const { token, store: storeData, last_bill_no, ...profile } = data
    const displayName =
      [profile.first_name, profile.last_name].filter(Boolean).join(' ') ||
      profile.username ||
      storeData?.name ||
      'Account'

    return {
      id: data._id,
      token,
      store: storeData,
      profile,
      label: displayName,
      lastUsedAt: Date.now(),
    }
  }

  const setSessionFromAccount = (account) => {
    if (!account?.token) return
    localStorage.setItem(AUTH_TOKEN_KEY, account.token)
    persistActiveAccount(account.id)
    setStore(account.store || {})
    setClientUserData(account.profile || {})
    setIsLoggedIn(true)
  }

  const upsertAccount = (account) => {
    const withoutActive = accounts.filter((item) => item.id !== account.id)
    const nextAccounts = [account, ...withoutActive]
    persistAccounts(nextAccounts)
    return nextAccounts
  }

  const getClientUser = async ({ silent } = {}) => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY)
    if (!token) return

    setUserLoading('fetch-user')
    try {
      const result = await apolloClient.query({
        query: GET_CURRENT_USER,
        fetchPolicy: 'network-only',
      })

      if (result?.data?.currentUser) setUserInfo(result.data.currentUser)
    } catch (err) {
      console.log('ClientUserProvider getClientUser', err)
      if (!silent && activeAccountId) {
        removeAccount(activeAccountId)
      }
    } finally {
      setUserLoading(false)
    }
  }

  useMount(() => {
    const storedAccounts = readStoredAccounts()
    setAccounts(storedAccounts)

    const storedActiveId = localStorage.getItem(ACTIVE_ACCOUNT_KEY)
    const fallbackAccount =
      storedAccounts.find((item) => item.id === storedActiveId) || storedAccounts[0]

    if (fallbackAccount?.token) {
      setSessionFromAccount(fallbackAccount)
      getClientUser()
    }
  })

  const signIn = async (username, password) => {
    try {
      setUserLoading('sign-in')

      const result = await apolloClient.mutate({
        mutation: SIGN_IN,
        variables: { username, password },
      })

      const userInfo = result?.data?.signIn

      if (userInfo) setUserInfo(userInfo)

      return userInfo
    } catch (err) {
      console.log('ClientUserProvider signIn', err)
      alert(err)
    } finally {
      setUserLoading(false)
    }
  }

  const signOut = ({ preserveAccounts } = {}) => {
    localStorage.removeItem(AUTH_TOKEN_KEY)
    persistActiveAccount(null)

    if (!preserveAccounts) {
      persistAccounts([])
      localStorage.removeItem(ACCOUNT_LIST_KEY)
    }

    setStore({})
    setClientUserData({})
    setIsLoggedIn(false)
  }

  const removeAccount = (accountId) => {
    const nextAccounts = accounts.filter((item) => item.id !== accountId)
    persistAccounts(nextAccounts)

    if (accountId === activeAccountId) {
      const nextActive = nextAccounts[0]
      if (nextActive) {
        setSessionFromAccount(nextActive)
      } else {
        signOut({ preserveAccounts: true })
      }
    }
  }

  const switchAccount = async (accountId) => {
    const target = accounts.find((item) => item.id === accountId)
    if (!target) return

    setUserLoading('switch-account')
    try {
      setSessionFromAccount(target)
      await getClientUser({ silent: true })
    } finally {
      setUserLoading(false)
    }
  }

  const changePassword = async (newPassword, confirmPassword) => {
    try {
      setUserLoading('change-password')

      const result = await apolloClient.mutate({
        mutation: CHANGE_PASSWORD,
        variables: { newPassword, confirmPassword },
      })

      const isSuccess = result?.changePassword?.status === 'success' || false
      if (isSuccess) return true
      if (!isSuccess) throw result
    } catch (err) {
      console.log('ClientUserProvider changePassword', err)
    } finally {
      setUserLoading(false)
    }
  }

  const setUserInfo = (data) => {
    const account = normalizeAccount(data)
    if (!account) return

    upsertAccount(account)
    setSessionFromAccount(account)
  }

  return (
    <ClientUserContext.Provider
      value={{
        isLoggedIn,
        clientUserData,
        store,
        userLoading,
        signIn,
        signOut,
        changePassword,
        accounts,
        activeAccountId,
        switchAccount,
        removeAccount,
      }}
    >
      {children}
    </ClientUserContext.Provider>
  )
}

const useClientUser = () => {
  const clientUser = useContext(ClientUserContext)
  if (clientUser == null) {
    throw new Error('clientUser() called outside of a AuthProvider?')
  }
  return clientUser
}

export { ClientUserProvider, useClientUser }
